import {
  BaseTextFieldProps,
  Box,
  FilledInputProps,
  InputBaseComponentProps,
  InputProps,
  OutlinedInputProps,
  SlotProps,
  TextField,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { FormHelperText } from '@mui/material';
import { InputRules } from '@/types/inputs';
import { buildInputRulesObject } from '@/utils/buildInputRulesObject';
import { camelCase } from '@/utils/stringCaser';
import { useState, useEffect } from 'react';

type CustomHelperTextProps = {
  errorMessage?: string;
  maxLength?: number;
  value: string;
  showHelperText?: boolean;
  max?: number;
  precision?: number;
};

function CustomHelperText({
  errorMessage,
  maxLength,
  value,
  showHelperText,
  max,
  precision,
}: CustomHelperTextProps) {
  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        margin: '2px',
        justifyContent: errorMessage ? 'space-between' : 'flex-end',
      }}>
      {errorMessage && (
        <FormHelperText
          sx={{
            flex: 1,
            width: '100%',
            margin: 0,
            marginLeft: '15px',
            color: 'error',
          }}>
          {errorMessage}
        </FormHelperText>
      )}
      {showHelperText && (
        <FormHelperText
          sx={{
            marginLeft: '5px',
            display: 'flex',
            flexWrap: 'nowrap',
            color: errorMessage ? 'error' : 'text.secondary',
          }}>
          Max: {max}% (up to {precision} decimal places)
        </FormHelperText>
      )}
      {maxLength && (
        <FormHelperText
          sx={{
            marginLeft: '5px',
            display: 'flex',
            flexWrap: 'nowrap',
            color: errorMessage ? 'error' : 'black',
          }}>
          {value?.length || 0}/{maxLength} characters
        </FormHelperText>
      )}
    </div>
  );
}

type SVPercentInputProps = {
  name: string;
  label: string;
  hideLabel?: boolean;
  disabled?: boolean;
  inputProps?:
    | Partial<FilledInputProps>
    | Partial<OutlinedInputProps>
    | Partial<InputProps>
    | undefined;
  htmlInputProps?: SlotProps<
    React.ElementType<
      InputBaseComponentProps | undefined,
      keyof React.JSX.IntrinsicElements
    >,
    InputBaseComponentProps,
    BaseTextFieldProps
  >;
  customOnChangeEvent?: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  >;
  rules?: InputRules;
  step?: string;
  min?: number;
  max?: number;
  precision?: number;
  showHelperText?: boolean;
};

export default function SVPercentageInput({
  name,
  label,
  hideLabel,
  disabled = false,
  inputProps,
  htmlInputProps,
  rules,
  customOnChangeEvent,
  step = '0.01',
  min = 0,
  max = 100,
  precision = 2,
  showHelperText = true,
}: SVPercentInputProps) {
  const methods = useFormContext();

  const enhancedRules: InputRules = {
    ...rules,
    validate: (value: string, formContext: any) => {
      if (value && value !== '') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue) && numValue > max) {
          return `Percentage cannot exceed ${max}%`;
        }
        if (!isNaN(numValue) && numValue <= 0) {
          return 'Please enter a number greater than 0';
        }
        if (!isNaN(numValue) && numValue < min) {
          return `Percentage must be at least ${min}%`;
        }
      }

      if (rules?.validate && typeof rules.validate === 'function') {
        const customValidation = rules.validate(value, formContext);
        if (customValidation !== true) {
          return customValidation;
        }
      }

      return true;
    },
  };

  const builtRulesObject = buildInputRulesObject(label, enhancedRules);
  const [displayValue, setDisplayValue] = useState<string>('');

  const formatValue = (value: string | number): string => {
    if (!value) return '';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '';

    return numValue.toFixed(precision);
  };

  const validateInput = (input: string): string => {
    let cleaned = input.replace(/[^0-9.]/g, '');

    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }

    if (parts.length === 2 && parts[1].length > precision) {
      cleaned = parts[0] + '.' + parts[1].substring(0, precision);
    }

    return cleaned;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    onChange: (value: any) => void,
  ) => {
    const input = e.target.value;
    const validatedInput = validateInput(input);

    setDisplayValue(validatedInput);

    if (customOnChangeEvent) {
      const customEvent = customOnChangeEvent(e);
      onChange(customEvent);
      return;
    }

    onChange(validatedInput);
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    onBlur: () => void,
  ) => {
    const input = e.target.value;
    if (input && !isNaN(parseFloat(input))) {
      const formatted = formatValue(input);
      setDisplayValue(formatted);
      const formValue = methods.getValues(name);
      if (formValue !== formatted) {
        methods.setValue(name, formatted);
      }
    }
    onBlur();
  };

  useEffect(() => {
    const currentValue = methods.getValues(name);
    if (currentValue !== undefined && currentValue !== '') {
      setDisplayValue(formatValue(currentValue));
    }
  }, [precision, methods, name]);

  useEffect(() => {
    const currentValue = methods.getValues(name);
    if (currentValue !== undefined && currentValue !== '') {
      setDisplayValue(formatValue(currentValue));
    }
  }, [methods, name]);

  return (
    <Controller
      control={methods.control}
      name={name}
      rules={builtRulesObject}
      render={({ field: { onChange, onBlur, ref }, fieldState: { error } }) => (
        <Box sx={{ marginBottom: 2 }}>
          <style>
            {`
              /* Hide number input spinner for all browsers */
              input[type='number']::-webkit-inner-spin-button,
              input[type='number']::-webkit-outer-spin-button {
                -webkit-appearance: none;
                margin: 0;
              }
              input[type='number'] {
                -moz-appearance: textfield;
              }

              /* Match MUI default input size and padding for adornment */
              input[type='number'] {
                padding-right: 2.5em;
              }
            `}
          </style>
          <TextField
            disabled={disabled}
            error={!!error?.message}
            id={camelCase(label)}
            slotProps={{
              input: {
                ...inputProps,
                endAdornment: <span style={{ fontWeight: 600 }}>%</span>,
                inputMode: 'decimal',
              },
              htmlInput: {
                ...htmlInputProps,
                step,
                min,
                max,
                type: 'text',
              },
            }}
            inputRef={ref}
            label={hideLabel ? '' : label}
            onChange={e => handleInputChange(e, onChange)}
            onBlur={e => handleBlur(e, onBlur)}
            value={displayValue}
            sx={{ width: '100%' }}
          />
          <CustomHelperText
            errorMessage={error?.message}
            maxLength={rules?.maxLength}
            value={displayValue}
            showHelperText={showHelperText}
            max={max}
            precision={precision}
          />
        </Box>
      )}
    />
  );
}
